import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { EyeIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { apiGetPatientsByPatientIdAlertsOptions } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { alertKinds, alertTypes } from "../../../constants/enum-to-text";
import {
  uniqueArrayValueOptions,
  uniqueValueOptions,
} from "../../../utils/filter-presets";
import { assert } from "../../../utils/utils";
import { PatientSubpage } from "../common/patient-subpage";
import { Alerts } from "./alerts";

export const AlertsTable = observer(function AlertsTable(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  assert(patient.data, "Patient data is missing");

  const [model] = useState(
    () =>
      new Alerts([], patient.api.permissions, patient.api.requireUserUUID()),
  );

  const { data } = useQuery(
    apiGetPatientsByPatientIdAlertsOptions({
      path: { patient_id: patient.patientId },
    }),
  );

  useEffect(() => {
    if (data) model.updateData(data.alerts);
  }, [data, model]);

  return (
    <PatientSubpage title={t`Alerts`} loading={!model.initialized}>
      <DataTable
        persistState
        model={model}
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "alert", other: "alerts" })}`
        }
        filterActions={
          <Button onClick={async () => await patient.togglePatientState()}>
            <Trans>Toggle patient state</Trans>
          </Button>
        }
        initialColumnFilters={[{ id: "seenMark", value: ["No"] }]}
        columns={[
          {
            header: <Trans>Timestamp</Trans>,
            attr: "alert_timestamp",
            type: "datetime",
            sort: true,
            initSort: "desc",
          },
          {
            header: <Trans>Kind</Trans>,
            attr: "alert_kind",
            type: "enum",
            values: alertKinds,
            filter: {
              kind: "multi",
              group: t`Alert`,
              options: new Map(Object.entries(alertKinds)),
            },
          },
          {
            header: <Trans>Variable</Trans>,
            attr: "alert_variable",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Alert`,
              options: uniqueValueOptions("alert_variable"),
            },
          },
          {
            header: <Trans>Type</Trans>,
            attr: "alert_type",
            type: "enum",
            values: alertTypes,
            filter: {
              kind: "multi",
              group: t`Alert`,
              options: new Map(Object.entries(alertTypes)),
            },
          },
          {
            header: <Trans>Value</Trans>,
            attr: "alert_value",
            type: "number",
            precision: 1,
          },
          {
            header: <Trans>Reason</Trans>,
            attr: "alert_reason",
            type: "string",
          },
          {
            header: <Trans>Seen</Trans>,
            attr: "seenMark",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Alert`,
              options: uniqueValueOptions("seenMark"),
            },
          },
          {
            header: <Trans>Tags</Trans>,
            attr: "alert_tags",
            type: "array",
            filter: {
              kind: "multi",
              group: t`Alert`,
              options: uniqueArrayValueOptions("alert_tags"),
            },
          },
          {
            header: <Trans>Mark seen</Trans>,
            sticky: "right",
            width: 60,
            cell: ({ item }) => (
              <Button
                variant="tertiary"
                size="sm"
                icon={EyeIcon}
                label={t`Seen`}
                onClick={() => model.markSeen(item.uuid)}
              />
            ),
          },
        ]}
      />
    </PatientSubpage>
  );
});
